import React, { useState, useEffect, useCallback } from 'react';
import Layout from "../../common/layout";
import styled, { createGlobalStyle, ThemeProvider } from 'styled-components';
import { Plus, Trash2, Eye, MessageSquare, Copy, CheckCircle, XCircle, GripVertical, GripHorizontal } from 'lucide-react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { postData } from '../../../../backend/api';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from "react-redux";
import axios from 'axios';
import { GetAccessToken } from '../../../../backend/authentication';
import { clearLogin } from '../../../../store/actions/login';
import { Footer } from '../../../elements/list/create/styles';
import { More } from '../../../elements/list/styles';
import { GetIcon } from '../../../../icons';
import FormInput from '../../../elements/input';
import CustomSelect from '../../../elements/select';

// --- STYLED COMPONENTS & THEME --- //

// This theme object is now used directly instead of a ThemeProvider
const theme = {
  colors: {
    primary: '#673ab7',
    primaryDark: '#512da8',
    primaryLight: '#d1c4e9',
    accent: '#ffc107',
    background: '#f0eef6',
    surface: '#ffffff',
    text: '#212121',
    secondaryText: '#757575',
    divider: '#dbdbdb',
    error: '#d32f2f',
    success: '#388e3c',
  },
  shadows: {
    sm: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
    md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
  },
  borderRadius: '10px',
};

// Main application container for the form page
const FormPageContainer = styled.div`
  display: flex;
  flex-direction: column;
  // min-height: 100vh;
  width: 100%;
  min-width: 600px;

  @media (max-width: 768px) {
    min-width: 100%;

  }
`;


// Header component
const Header = styled.header`
  background-color: ${theme.colors.surface};
  padding: 1rem 2rem;
  border-bottom: 1px solid ${theme.colors.divider};
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: ${theme.shadows.sm};
  position: sticky;
  top: 0;
  z-index: 1000;
`;

const Logo = styled.h1`
  font-size: 1.5rem;
  font-weight: 500;
  color: ${theme.colors.primary};
  margin: 0 !important;
`;

const Nav = styled.nav`
  display: flex;
  gap: 1rem;
`;

const NavButton = styled.button`
  background-color: ${props => props.active ? theme.colors.primaryLight : 'transparent'};
  color: ${props => props.active ? theme.colors.primaryDark : theme.colors.secondaryText};
  border: 1px solid ${props => props.active ? theme.colors.primaryLight : theme.colors.divider};
  padding: 0.5rem 1.5rem;
  border-radius: ${theme.borderRadius};
  cursor: pointer;
  font-weight: 500;
  transition: all 0.2s ease-in-out;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &:hover {
    background-color: ${theme.colors.primaryLight};
    color: ${theme.colors.primaryDark};
  }
`;

// Main content area
const MainContent = styled.main`
  flex-grow: 1;
  padding: 2rem;
  display: flex;
  justify-content: center;

  @media (max-width: 768px){
    padding: 0;
  }
`;

const FormBuilderContainer = styled.div`
  width: 100%;
  max-width: 800px;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const Card = styled.div`
  background-color: ${theme.colors.surface};
  border-radius: ${theme.borderRadius};
  border: 1px solid ${theme.colors.divider};
  border-top: 10px solid ${theme.colors.primary};
  padding: 1.5rem;
  box-shadow: ${theme.shadows.md};
  transition: box-shadow 0.3s ease;

  &:focus-within {
    border-color: ${theme.colors.primary};
    box-shadow: ${theme.shadows.lg};
  }

  @media (max-width: 768px) {
    padding: 0.5rem;
  }
`;

const QuestionCard = styled(Card)`
  border-top: none;
  border-left: 5px solid transparent;
  transition: border-left 0.3s ease;
  display: flex;
  flex-direction: column;
  // gap: 1rem;
  margin-bottom: 1rem;
  padding-top: 0;

  &:focus-within {
     border-left: 5px solid ${theme.colors.primary};
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: none;
  border-bottom: 1px solid ${theme.colors.divider};
  font-size: 1rem;
  background-color: transparent;
  transition: border-color 0.3s ease;

  &:focus {
    outline: none;
    border-bottom: 2px solid ${theme.colors.primary};
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.75rem 0;
  border: none;
  border-bottom: 1px solid ${theme.colors.divider};
  font-size: 1rem;
  background-color: transparent;
  resize: vertical;
  min-height: 40px;
  text-align: left;

  &::placeholder {
    color: ${theme.colors.secondaryText};
    opacity: 0.7;
  }

  &:focus {
    outline: none;
    border-bottom: 2px solid ${theme.colors.primary};
  }
`;

const Select = styled.select`
  padding: 0.5rem 1rem;
  border: 1px solid ${theme.colors.divider};
  border-radius: ${theme.borderRadius};
  font-size: 0.9rem;
  background-color: ${theme.colors.surface};
  cursor: pointer;
`;

const QuestionFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid ${theme.colors.divider};
`;

const IconButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: ${theme.colors.secondaryText};
  padding: 0.5rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background-color: ${theme.colors.background};
    color: ${theme.colors.primary};
  }
`;

const AddQuestionButton = styled(IconButton)`
    position: sticky;
    top: 80px;
    align-self: flex-end;
    background-color: ${theme.colors.surface};
    color: ${theme.colors.primary};
    border-radius: 50%;
    box-shadow: ${theme.shadows.lg};
    padding: 1rem;
    margin-bottom: 1rem;

    &:hover {
      transform: scale(1.1);
      background-color: ${theme.colors.primaryLight};
    }
`;

const OptionContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.5rem;
`;

const AddOptionButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: ${theme.colors.primary};
  font-weight: 500;
  margin-top: 0.5rem;
`;

const ToggleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: ${theme.colors.secondaryText};
  font-size: 0.9rem;
`;

const Switch = styled.label`
  position: relative;
  display: inline-block;
  width: 40px;
  height: 20px;

  & input {
    opacity: 0;
    width: 0;
    height: 0;
  }

  & span {
    position: absolute;
    cursor: pointer;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: #ccc;
    transition: .4s;
    border-radius: 20px;
  }

  & span:before {
    position: absolute;
    content: "";
    height: 16px;
    width: 16px;
    left: 2px;
    bottom: 2px;
    background-color: white;
    transition: .4s;
    border-radius: 50%;
  }

  input:checked + span {
    background-color: ${theme.colors.primary};
  }

  input:checked + span:before {
    transform: translateX(20px);
  }
`;

const FormViewerContainer = styled.div`
  width: 100%;
  height: 100%;
  max-width: 700px;
  min-width: 50vw;
  border: 1px solid #d9d9d9;
  background-color: ${theme.colors.surface};
  border-radius: ${theme.borderRadius};
  padding: 2rem;
  margin: 0 10px;
  box-shadow: ${theme.shadows.md};

  @media (max-width: 768px){
    min-width: 100%;
    max-width: 100%;
  }
`;

const FormViewerHeader = styled.div`
  border-bottom: 10px solid ${theme.colors.primary};
  padding-bottom: 1rem;
  margin-bottom: 1.5rem;
`;

const FormTitle = styled.h1`
  margin: 20px 0 !important;
`;

const FormDescription = styled.p`
  font-size: 1rem;
  color: ${theme.colors.text};
`;

const QuestionViewerCard = styled.div`
  margin-bottom: 1.5rem;
  padding: 1.5rem;
  border: 1px solid ${theme.colors.divider};
  border-radius: ${theme.borderRadius};
`;

const QuestionTitle = styled.h3`
  font-size: 1.2rem;
  margin-bottom: 1rem;
  span {
    color: ${theme.colors.error};
  }
`;

const SubmitButton = styled.button`
  background-color: ${theme.colors.primary};
  color: white;
  border: none;
  padding: 0.75rem 2rem;
  border-radius: ${theme.borderRadius};
  cursor: pointer;
  font-size: 1rem;
  font-weight: 500;
  transition: background-color 0.2s;

  &:hover {
    background-color: ${theme.colors.primaryDark};
  }
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0,0,0,0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2000;
`;

const ModalContent = styled.div`
  background: white;
  padding: 2rem;
  border-radius: ${theme.borderRadius};
  text-align: center;
  max-width: 400px;
  box-shadow: ${theme.shadows.lg};
`;

const ResponseContainer = styled.div`
    width: 100%;
    max-width: 900px;
`;

const ResponseCard = styled(Card)`
  border-top: 1px solid ${theme.colors.divider};
  margin-bottom: 1rem;
`;

const CustomAlert = styled.div`
  position: fixed;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  background-color: ${props => props.type === 'success' ? theme.colors.success : theme.colors.error};
  color: white;
  padding: 1rem 2rem;
  border-radius: ${theme.borderRadius};
  box-shadow: ${theme.shadows.lg};
  z-index: 9999;
  opacity: ${props => props.show ? 1 : 0};
  transition: opacity 0.3s ease-in-out;
`;

const DragHandle = styled.div`
  cursor: grab;
  color: ${theme.colors.secondaryText};
  padding: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  touch-action: none;
  
  &:hover {
    color: ${theme.colors.primary};
  }
`;

const QuestionContent = styled.div`
  flex: 1;
`;

const QuestionAndSelect = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const QuillEditor = styled(ReactQuill)`
  .ql-container {
    min-height: 80px;
    border: none;
    border-bottom: 1px solid ${theme.colors.divider};
  }

  &.quill-readonly{
    .ql-container {
      min-height: 20px;
      border: none;
      // border-bottom: 1px solid ${theme.colors.divider};
    }
  }
  
  .ql-toolbar {
    border: none;
    border-bottom: 1px solid ${theme.colors.divider};
  }

  .ql-editor {
    padding: 0.75rem;
    text-align: left!important;
    font-size: 1rem;
  }

`;

const QuestionsContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

// --- REACT COMPONENTS --- //

const Question = ({ question, updateQuestion, deleteQuestion, index }) => {
  const {t} = useTranslation();

  const [QuestionType] = useState([
    { id: 'SHORT_ANSWER', value: t("Short Answer") },
    { id: 'PARAGRAPH', value: t("Paragraph") },
    { id: 'MULTIPLE_CHOICE', value: t("Multiple Choice") },
    { id: 'CHECKBOXES', value: t("Checkboxes") },
    { id: 'DROPDOWN', value: t("Dropdown") }
  ])

  const handleInputChange = (e) => {
    updateQuestion(question.id, { ...question, questionText: e.target.value });
  };

  const handleTypeChange = (index, newValue) => {
    updateQuestion(question.id, { ...question, questionType: newValue.id, options: [] });
  };
  
  const handleRequiredChange = (e) => {
    updateQuestion(question.id, { ...question, isRequired: e.target.checked });
  };

  const handleDescriptionChange = (e) => {
    updateQuestion(question.id, { ...question, isDescription: e.target.checked });
  };

  const handleDescriptionValueChange = (value) => {
    updateQuestion(question.id, { ...question, description: value });
  };

  const addOption = () => {
    const newOption = { id: `opt_${Date.now()}`, text: `Option ${question.options.length + 1}` };
    updateQuestion(question.id, { ...question, options: [...question.options, newOption] });
  };

  const updateOption = (optionId, newText) => {
    const updatedOptions = question.options.map(opt => 
      opt.id === optionId ? { ...opt, text: newText } : opt
    );
    updateQuestion(question.id, { ...question, options: updatedOptions });
  };

  const deleteOption = (optionId) => {
    const updatedOptions = question.options.filter(opt => opt.id !== optionId);
    updateQuestion(question.id, { ...question, options: updatedOptions });
  };

  const renderOptions = () => {
    return (
      <div>
        {question.options.map((opt, index) => (
          <OptionContainer key={opt.id}>
             <input type={question.questionType === 'MULTIPLE_CHOICE' ? 'radio' : 'checkbox'} disabled name={`option_preview_${question.id}`} />
            <Input 
              type="text" 
              value={opt.text}
              onChange={(e) => updateOption(opt.id, e.target.value)}
              placeholder={`${t("Option")} ${index + 1}`}
              style={{ fontSize: '1rem', borderBottom: '1px solid #eee' }}
            />
            <IconButton onClick={() => deleteOption(opt.id)}><XCircle size={18} /></IconButton>
          </OptionContainer>
        ))}
        <AddOptionButton onClick={addOption}>{t("Add option")}</AddOptionButton>
      </div>
    );
  };

  return (
    <Draggable draggableId={question.id} index={index}>
      {(provided, snapshot) => (
        <QuestionCard
          ref={provided.innerRef}
          {...provided.draggableProps}
          style={{
            ...provided.draggableProps.style,
            opacity: snapshot.isDragging ? 0.8 : 1,
            transform: snapshot.isDragging ? provided.draggableProps.style.transform : 'none',
            transition: 'opacity 0.2s ease'
          }}
        >
          <DragHandle {...provided.dragHandleProps}>
            <GripHorizontal size={20} />
          </DragHandle>
          <QuestionContent>
            <QuestionAndSelect>
              <Input 
                type="text" 
                value={question.questionText}
                onChange={handleInputChange}
                placeholder={t("Question")}
                style={{flexGrow: 1}}
              />
              <CustomSelect customClass="half" placeHolder={""} apiType={"JSON"} selectApi={QuestionType} value={question.questionType} onSelect={handleTypeChange} required={true} />
              {/* <Select value={question.questionType} onChange={handleTypeChange}>
                <option value="SHORT_ANSWER">{t("Short Answer")}</option>
                <option value="PARAGRAPH">{t("Paragraph")}</option>
                <option value="MULTIPLE_CHOICE">{t("Multiple Choice")}</option>
                <option value="CHECKBOXES">{t("Checkboxes")}</option>
                <option value="DROPDOWN">{t("Dropdown")}</option>
              </Select> */}
            </QuestionAndSelect>
            {question.isDescription && (
              <QuillEditor
                value={question.description || ''}
                onChange={handleDescriptionValueChange}
                placeholder={t("Question description")}
                modules={{
                  toolbar: [
                    ['bold', 'italic', 'underline', 'strike'],
                    [{'size': []}],
                    ['code-block'],
                    [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                    [{'align':[]}],
                    ['link'],
                    ['clean']
                ]
                }}
              />
            )}
            <div style={{ marginTop: '1.5rem' }}>
              {question.questionType === 'SHORT_ANSWER' && <Input type="text" placeholder={t("Short answer text")} disabled />}
              {question.questionType === 'PARAGRAPH' && <TextArea placeholder={t("Long answer text")} disabled />}
              {(question.questionType === 'MULTIPLE_CHOICE' || question.questionType === 'CHECKBOXES' || question.questionType === 'DROPDOWN') && renderOptions()}
            </div>
            <QuestionFooter>
              <ToggleContainer>
                {t("Required")}
                <Switch>
                  <input type="checkbox" checked={question.isRequired} onChange={handleRequiredChange} />
                  <span />
                </Switch>
              </ToggleContainer>
              <ToggleContainer style={{paddingLeft: '0.5rem'}}>
                {t("description")}
                <Switch>
                  <input type="checkbox" checked={question.isDescription} onChange={handleDescriptionChange} />
                  <span />
                </Switch>
              </ToggleContainer>
              <IconButton onClick={() => deleteQuestion(question.id)}>
                <Trash2 size={20} />
              </IconButton>
            </QuestionFooter>
          </QuestionContent>
        </QuestionCard>
      )}
    </Draggable>
  );
};

const FormBuilder = ({ form, setForm, saveForm }) => {
  const { t } = useTranslation();
    const updateFormTitle = (title) => setForm(prev => ({ ...prev, title }));
    const updateFormDescription = (description) => setForm(prev => ({ ...prev, description }));

    const addQuestion = () => {
        const newQuestion = {
            id: `q_${Date.now()}`,
            questionText: '',
            questionType: 'SHORT_ANSWER',
            options: [],
            isRequired: false
        };
        setForm(prev => ({ ...prev, questions: [...prev.questions, newQuestion] }));
    };

    const updateQuestion = (id, updatedQuestion) => {
        setForm(prev => ({
            ...prev,
            questions: prev.questions.map(q => q.id === id ? updatedQuestion : q)
        }));
    };

    const deleteQuestion = (id) => {
        setForm(prev => ({
            ...prev,
            questions: prev.questions.filter(q => q.id !== id)
        }));
    };

    const handleDragEnd = (result) => {
        if (!result.destination) return;
        
        const items = Array.from(form.questions);
        const [reorderedItem] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, reorderedItem);
        
        setForm(prev => ({
            ...prev,
            questions: items
        }));
    };

    return (
        <FormBuilderContainer>
            <Card>
                <Input 
                    value={t(form.title)}
                    onChange={(e) => updateFormTitle(e.target.value)}
                    placeholder={t("Form title")}
                />
                <QuillEditor
                    value={form.description}
                    onChange={updateFormDescription}
                    placeholder={t("Form description")}
                    modules={{
                        toolbar: [
                            ['bold', 'italic', 'underline', 'strike'],
                            [{'size': []}],
                            ['code-block'],
                            [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                            [{'align':[]}],
                            ['link'],
                            ['clean']
                        ]
                    }}
                />
            </Card>
            <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId="questions" key={form.questions.length}>
                    {(provided) => (
                        <QuestionsContainer
                            {...provided.droppableProps}
                            ref={provided.innerRef}
                        >
                            {form.questions.map((q, index) => (
                                <Question 
                                    key={q.id}
                                    question={q}
                                    updateQuestion={updateQuestion}
                                    deleteQuestion={deleteQuestion}
                                    index={index}
                                />
                            ))}
                            {provided.placeholder}
                        </QuestionsContainer>
                    )}
                </Droppable>
            </DragDropContext>
            <AddQuestionButton onClick={addQuestion} title="Add Question">
                <Plus size={24} />
            </AddQuestionButton>
            {/* <SubmitButton onClick={saveForm}>{t("Save Form")}</SubmitButton> */}
        </FormBuilderContainer>
    );
};

const FormViewer = ({ form, handleFormSubmission, showAlert, response }) => {
  const { t } = useTranslation();
    const [responses, setResponses] = useState(response ? response :{});
    const [isSubmitted, setIsSubmitted] = useState((response && handleFormSubmission) ? true : false);


    const handleResponseChange = (questionId, value) => {
        setResponses(prev => ({ ...prev, [questionId]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        console.log("responses", responses)

        if(handleFormSubmission){
          for (const question of form.questions) {
              if (question.isRequired) {
                  const response = responses[question.id];
                  if (!response || (Array.isArray(response) && response.length === 0)) {
                      showAlert(`Question "${question.questionText}" is required.`, 'error');
                      return;
                  }
              }
          }

          const success = await handleFormSubmission(responses);
          console.log(success)
          if (success) {
              setIsSubmitted(true);
          }
        }
    };
    
    if (isSubmitted) {
        return (
            <FormViewerContainer>
                <div style={{textAlign: 'center'}}>
                    <CheckCircle size={48} color={theme.colors.success} />
                    <FormTitle>{t("responseSubmit")}</FormTitle>
                    <FormDescription>{t("responseSubmitDescription")}</FormDescription>
                </div>
            </FormViewerContainer>
        );
    }
    
    const renderQuestionInput = (q) => {
        switch (q.questionType) {
            case 'SHORT_ANSWER':
                return <Input type="text" disabled={handleFormSubmission ? false : true} placeholder={`${t("Write your answer here")}...`} onChange={(e) => handleResponseChange(q.id, e.target.value)} required={q.isRequired}/>;
            case 'PARAGRAPH':
                return <TextArea disabled={handleFormSubmission ? false : true} placeholder={`${t("Write your answer here")}...`} onChange={(e) => handleResponseChange(q.id, e.target.value)} required={q.isRequired}/>;
            case 'MULTIPLE_CHOICE':
                return q.options.map(opt => (
                    <div style={{marginBottom: '0.5rem'}} key={opt.id}>
                        <input disabled={handleFormSubmission ? false : true} type="radio" id={opt.id} name={q.id} value={opt.text} onChange={(e) => handleResponseChange(q.id, e.target.value)} required={q.isRequired} />
                        <label htmlFor={opt.id} style={{marginLeft: '8px'}}>{opt.text}</label>
                    </div>
                ));
            case 'CHECKBOXES':
                return q.options.map(opt => (
                    <div style={{marginBottom: '0.5rem'}} key={opt.id}>
                        <input disabled={handleFormSubmission ? false : true} type="checkbox" id={opt.id} name={q.id} value={opt.text} onChange={(e) => {
                            const currentRes = responses[q.id] || [];
                            const newRes = e.target.checked ? [...currentRes, e.target.value] : currentRes.filter(v => v !== e.target.value);
                            handleResponseChange(q.id, newRes);
                        }}/>
                        <label htmlFor={opt.id} style={{marginLeft: '8px'}}>{opt.text}</label>
                    </div>
                ));
            case 'DROPDOWN':
                return (
                    <Select onChange={(e) => handleResponseChange(q.id, e.target.value)} required={q.isRequired} defaultValue="">
                        <option value="" disabled>{t("Select an option")}</option>
                        {q.options.map(opt => (
                            <option disabled={handleFormSubmission ? false : true} key={opt.id} value={opt.text}>{opt.text}</option>
                        ))}
                    </Select>
                );
            default:
                return null;
        }
    }
    
    return (
      <FormViewerContainer>
        <FormViewerHeader>
          <FormTitle>{t(form?.title)}</FormTitle>
          {form?.description && (
                <div style={{ marginBottom: '1rem' }}>
                  <QuillEditor
                    className="quill-readonly"
                    value={form.description}
                    readOnly
                    theme="bubble"
                    modules={{
                      toolbar: false,
                    }}
                  />
                </div>
              )}
        </FormViewerHeader>
        <form onSubmit={handleSubmit}>
          {form?.questions?.map(q => (
            <QuestionViewerCard key={q.id}>
              <QuestionTitle>{q.questionText} {q.isRequired && <span>*</span>}</QuestionTitle>
              {q.isDescription && q.description && (
                <div style={{ marginBottom: '1rem' }}>
                  <QuillEditor
                    className="quill-readonly"
                    value={q.description}
                    readOnly
                    theme="bubble"
                    modules={{
                      toolbar: false,
                    }}
                  />
                </div>
              )}
              {renderQuestionInput(q)}
            </QuestionViewerCard>
          ))}
          {handleFormSubmission && <SubmitButton type="submit">{t("Submit")}</SubmitButton>}
        </form>
      </FormViewerContainer>
    );
};

const ResponsesViewer = ({ form, response }) => {

  const {t} = useTranslation();

    if (!response) {
        return (
             <ResponseContainer>
                <Card>
                    <FormTitle>{form.title} - {t("answer")}</FormTitle>
                    <FormDescription>{t("No answer yet")}.</FormDescription>
                </Card>
            </ResponseContainer>
        )
    }

    return (
        <ResponseContainer>
            <Card>
                <FormTitle>{form.title} - {t("answer")}</FormTitle>
                {/* <FormDescription>{responses.length} response(s)</FormDescription> */}
            </Card>
            {/* {responses.map((res, index) => ( */}
                <ResponseCard >
                    {/* <h4>Response #{index + 1}</h4> */}
                    {form?.questions?.map((q, idx) => (
                        <div key={q.id} style={{marginTop: '1rem'}}>
                            <strong>Q{idx+1}. {q?.questionText}</strong>
                            <p><span style={{fontWeight: 600}}>{t("answer")}:</span> {Array.isArray(response[q.id]) ? response[q.id].join(', ') : response[q.id] || t('No answer')}</p>
                        </div>
                    ))}
                </ResponseCard>
            {/* ))} */}
        </ResponseContainer>
    );
};


// This is the main component for your form page.
function AddForm(props) {
  // Assume formId might come from URL params in a real app
  const [formId, setFormId] = useState(props.formId || null); 
  const params = useParams();

  const dispatch = useDispatch()
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [form, setForm] = useState({
      title: 'My New Form',
      description: '',
      questions: [],
  });
  
  const [allResponses, setAllResponses] = useState([]);
  const [activeView, setActiveView] = useState('builder');
  const [isLoading, setIsLoading] = useState(true);
  const [showShareModal, setShowShareModal] = useState(false);
  const [alertInfo, setAlertInfo] = useState({ show: false, message: '', type: 'success' });

  const showAlert = (message, type = 'success') => {
    setAlertInfo({ show: true, message, type });
    setTimeout(() => {
        setAlertInfo({ show: false, message: '', type: 'success' });
    }, 3000);
  };

  // --- Backend Integration Placeholders --- //

  const fetchFormStructure = async (formId) => {
    try {
      setIsLoading(true);
      let token = GetAccessToken();
      const response = await axios.get(`${process.env.REACT_APP_API}emp_dev_form?id=${formId}`, {
              headers: {
                "Content-Type": "application/json",
                "x-access-token": token,
              },
            });
            if (response.status === 401) {
              try {
                dispatch(clearLogin());
                navigate("/");
                navigate(0);
              } catch (error) {}
            }
            if(response.status === 200 && response.data){
              setForm(response.data);
            }
            setIsLoading(false);
    } catch (error) {
      console.error("Error fetching form structure:", error);
    }
  }

  // Fetch initial form structure
  useEffect(() => {
    console.log(`Fetching form structure for formId: ${formId}`);
    // *** YOUR CODE HERE ***
    // Replace this with your API call to fetch the form structure
    // e.g., fetch(`/api/forms/${formId}`).then(res => res.json()).then(data => setForm(data));

    if(formId)
      fetchFormStructure(formId);
    else
      setIsLoading(false);

  }, [formId]);

  // Fetch existing responses
  useEffect(() => {
      if (activeView === 'responses') {
        console.log(`Fetching responses for formId: ${formId}`);
        // *** YOUR CODE HERE ***
        // Replace this with your API call to fetch responses
        // e.g., fetch(`/api/forms/${formId}/responses`).then(res => res.json()).then(data => setAllResponses(data));
        
        // Simulating fetching responses
        // setAllResponses([{...}, {...}]);
      }
  }, [formId, activeView]);

  // Save the form structure
  const handleSaveForm = useCallback(async () => {
    try {
      props.handleSubmit(form);
    } catch (error) {
      
    } 
  }, [form]);

  // Handle a new form submission from a user
  const handleFormSubmission = async (newResponse) => {
      console.log("Submitting new response to backend:", newResponse);
      // *** YOUR CODE HERE ***
      // Replace with your API call to submit a response
      /*
      try {
        const response = await fetch(`/api/forms/${formId}/responses`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(newResponse)
        });
        if (!response.ok) throw new Error('Network response was not ok');
        showAlert("Response submitted!", 'success');
        return true; // Indicate success
      } catch (error) {
        console.error("Error submitting response: ", error);
        showAlert("Error submitting response.", 'error');
        return false; // Indicate failure
      }
      */
      setAllResponses(prev => [...prev, newResponse]); // Add to local state for demo
      showAlert("Response submitted successfully! (Simulated)", 'success');
      return true; // Simulate success
  };

  const checkDisabled = useCallback(() => {
    if((!form.title || form.title.trim() === "") || !form.questions || !(form.questions.length > 0)) {
      return true;
    }
    if (form.questions.some(q =>
      (!q.questionText || q.questionText.trim() === "") ||
      (q.isDescription && (!q.description || q.description.trim() === "" || q.description===null)) ||
      ((q.questionType === 'MULTIPLE_CHOICE' || q.questionType === 'CHECKBOXES' || q.questionType === 'DROPDOWN') && q.options.length === 0)
    )) {
      return true;
    }
    return false;
  }, [form]);

  const getShareableLink = () => {
      return window.location.href;
  };

  const copyToClipboard = () => {
    const link = getShareableLink();
    const textArea = document.createElement("textarea");
    textArea.value = link;
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    try {
        document.execCommand('copy');
        showAlert("Link copied to clipboard!", "success");
    } catch (err) {
        console.error('Could not copy text: ', err);
        showAlert("Failed to copy link.", "error");
    }
    document.body.removeChild(textArea);
  };
  
  if (isLoading) {
      return <div style={{display:'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', width: '100%', fontSize:'1.5rem'}}>{t("Loading Form")}...</div>;
  }
  
  return (
    <FormPageContainer>
      <CustomAlert show={alertInfo.show} type={alertInfo.type}>{alertInfo.message}</CustomAlert>
      {/* <Header>
          <Logo>{t("Add Form")}</Logo>
           <Nav>
                <NavButton active={activeView === 'builder'} onClick={() => setActiveView('builder')}>
                    <Plus size={16}/> Questions
                </NavButton>
               <NavButton active={activeView === 'preview'} onClick={() => setActiveView('preview')}>
                    <Eye size={16}/> Preview
                </NavButton>
                <IconButton onClick={() => setShowShareModal(true)} title="Share Form"><Copy size={20}/></IconButton>
          </Nav>
      </Header> */}
      <MainContent>
          <FormBuilder form={form} setForm={setForm} saveForm={handleSaveForm} />
          {/* {activeView === 'preview' && <FormViewer form={form} handleFormSubmission={handleFormSubmission} showAlert={showAlert} />} */}
          {/* {activeView === 'responses' && <ResponsesViewer form={form} responses={allResponses} />} */}
      </MainContent>

      {showShareModal && (
          <Modal onClick={() => setShowShareModal(false)}>
              <ModalContent onClick={e => e.stopPropagation()}>
                  <h2>Share Form</h2>
                  <p>Anyone with the link can view and respond to this form.</p>
                  <Input readOnly value={getShareableLink()} />
                  <SubmitButton style={{marginTop: '1rem'}} onClick={copyToClipboard}>Copy Link</SubmitButton>
              </ModalContent>
          </Modal>
      )}

      <Footer>
                {/* {delPrivilege && !(props.bulkUpload ?? false) && (
                  <More
                    key={`delete`}
                    onClick={() => {
                      setMessage({
                        type: 2,
                        content: t("deleteItem"),
                        proceed: t("delete"),
                        onProceed: deleteHandler ? deleteHandler : () => alert("not a function"),
                        data: { ...formValues, _id: updateId },
                      });
                    }}
                    className="delete left"
                  >
                    <GetIcon icon={"delete"} />
                  </More>
                )} */}
                <FormInput type="close" value={t("cancel")} onChange={props.closeModal} />
                <FormInput disabled={checkDisabled()} type="submit" name="submit" value={t("submit")} onChange={handleSaveForm} />
              </Footer>
    </FormPageContainer>
  );
}

export default Layout(AddForm);

export {FormViewer, ResponsesViewer};

